<template>
    <div>
        <b-alert variant="warning" show class="mb-3">
            <i class="bi bi-exclamation-triangle-fill mr-1"></i>
            <strong>되돌릴 수 없는 작업입니다.</strong>
            변경 후 캠페인에 <u>없는 문항</u>의 답변과 <u>감사자 평가 전량</u>은 영구 삭제됩니다.
            삭제된 원본은 실행 이력에 백업되지만, <strong>복구는 자동으로 되지 않습니다</strong>
            (담당자가 직접 복원해야 합니다).
            반드시 미리보기로 삭제 건수를 확인하고 관계자 동의를 받은 뒤 실행하세요.
        </b-alert>

        <!-- ── 대상 선택 ── -->
        <b-card class="mb-3">
            <b-row>
                <b-col md="3">
                    <b-form-group label="기간" class="mb-2">
                        <b-form-select v-model="selectedPeriod" :disabled="isBusy || !periods.length" @change="onPeriodChange">
                            <b-form-select-option v-if="!periods.length" :value="null" disabled>
                                {{ loadingPeriods ? '불러오는 중...' : '기간이 없습니다' }}
                            </b-form-select-option>
                            <b-form-select-option v-for="p in periods" :key="p" :value="p">
                                {{ p }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </b-col>
                <b-col md="5">
                    <b-form-group label="프로젝트" class="mb-2">
                        <auto-complete-select v-model="selectedProjectCode"
                                              :options="projectOptions"
                                              option-value-key="id"
                                              :loading="loadingProjects"
                                              :disabled="isBusy"
                                              :placeholder="projectPlaceholder"
                                              list-max-height="360px"
                                              clearable
                                              @input="onProjectChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="업체 (현재 캠페인)" class="mb-2">
                        <!-- 한 프로젝트에 업체가 수백 개일 수 있어 검색 드롭다운을 쓴다.
                             업체명과 company_id 를 모두 text 에 담아 둘 다로 검색되게 한다. -->
                        <auto-complete-select v-model="selectedCompanyKey"
                                              :options="companyOptions"
                                              option-value-key="id"
                                              :loading="loadingCompanies"
                                              :disabled="isBusy || !selectedProjectCode"
                                              :placeholder="companyPlaceholder"
                                              list-max-height="360px"
                                              clearable
                                              @input="onCompanyChange"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row class="align-items-end">
                <b-col md="4">
                    <b-form-group label="변경 전 캠페인" class="mb-0">
                        <b-form-input :value="fromCampaignText" disabled/>
                    </b-form-group>
                </b-col>
                <b-col md="1" class="text-center pb-2 d-none d-md-block">
                    <i class="bi bi-arrow-right" style="font-size: 1.4rem;"></i>
                </b-col>
                <b-col md="4">
                    <b-form-group label="변경 후 캠페인" class="mb-0">
                        <b-form-select v-model="toCampaignId"
                                       :disabled="isBusy || !selectedCompany || loadingCampaigns"
                                       @change="resetPreview"
                        >
                            <b-form-select-option :value="null" disabled>
                                {{ toCampaignPlaceholder }}
                            </b-form-select-option>
                            <b-form-select-option v-for="c in toCampaignOptions"
                                                  :key="c.id" :value="c.id"
                            >
                                {{ c.campaign_name }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </b-col>
                <b-col md="3" class="text-right">
                    <b-button variant="outline-primary" :disabled="!canPreview || isBusy"
                              @click="doPreview"
                    >
                        <b-spinner v-if="previewLoading" small class="mr-1"/>
                        미리보기
                    </b-button>
                </b-col>
            </b-row>
        </b-card>

        <!-- ── 미리보기 결과 ── -->
        <template v-if="preview">
            <b-alert v-for="(w, i) in preview.warnings" :key="`w-${i}`"
                     variant="warning" show class="py-2"
            >
                <i class="bi bi-exclamation-circle mr-1"></i>{{ w }}
            </b-alert>

            <b-card class="mb-3">
                <h5 class="mb-3">
                    영향 범위
                    <small class="text-muted ml-2">
                        {{ preview.from_campaign_name }} &rarr; {{ preview.to_campaign_name }}
                    </small>
                </h5>

                <b-row class="text-center mb-3">
                    <b-col>
                        <div class="summary-box">
                            <div class="summary-label">현재 답변</div>
                            <div class="summary-value">{{ preview.at_from }}</div>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="summary-box border-success">
                            <div class="summary-label">유지</div>
                            <div class="summary-value text-success">{{ preview.keep_count }}</div>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="summary-box" :class="preview.delete_count ? 'border-danger' : ''">
                            <div class="summary-label">영구 삭제</div>
                            <div class="summary-value" :class="preview.delete_count ? 'text-danger' : ''">
                                {{ preview.delete_count }}
                            </div>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="summary-box"
                             :class="preview.evaluation_delete_count ? 'border-danger' : ''"
                        >
                            <div class="summary-label">감사자 평가 삭제</div>
                            <div class="summary-value"
                                 :class="preview.evaluation_delete_count ? 'text-danger' : ''"
                            >
                                {{ preview.evaluation_delete_count }}
                            </div>
                        </div>
                    </b-col>
                    <b-col>
                        <div class="summary-box">
                            <div class="summary-label">첨부</div>
                            <div class="summary-value">{{ preview.attachment_storage_count }}</div>
                        </div>
                    </b-col>
                </b-row>

                <b-alert v-if="preview.evaluation_delete_count" variant="danger" show class="py-2">
                    <i class="bi bi-x-octagon-fill mr-1"></i>
                    감사자가 이미 매긴 평가 <strong>{{ preview.evaluation_delete_count }}건</strong>이
                    삭제됩니다. 재매핑이 불가능한 구조라 감사자가 <strong>재평가</strong>해야 합니다 —
                    실행 전 반드시 동의를 받으세요.
                </b-alert>

                <!-- 삭제될 문항 목록 -->
                <div v-if="preview.delete_sub_codes && preview.delete_sub_codes.length">
                    <b-button variant="link" class="p-0 mb-2" @click="showDeleteList = !showDeleteList">
                        <i class="bi" :class="showDeleteList ? 'bi-caret-down-fill' : 'bi-caret-right-fill'"></i>
                        삭제될 문항 {{ preview.delete_sub_codes.length }}개 보기
                    </b-button>
                    <b-collapse v-model="showDeleteList">
                        <div class="delete-list p-2 mb-2">
                            <b-badge v-for="code in preview.delete_sub_codes" :key="code"
                                     variant="light" class="mr-1 mb-1 border"
                            >
                                {{ code }}
                            </b-badge>
                        </div>
                        <p class="text-muted small mb-0">
                            변경 후 캠페인에 없는 문항입니다. 이 문항들의 답변은 삭제되며,
                            원본은 실행 이력에 백업됩니다.
                        </p>
                    </b-collapse>
                </div>
                <p v-else class="text-success mb-0">
                    <i class="bi bi-check-circle mr-1"></i>삭제되는 답변이 없습니다 — 전부 유지됩니다.
                </p>
            </b-card>

            <b-card class="mb-3">
                <h6 class="mb-2">함께 이동하는 배정</h6>
                <p class="small text-muted">
                    답변·멤버십은 (업체, 캠페인) 단위로 1벌이라 프로젝트별로 나눌 수 없습니다.
                    아래 프로젝트의 배정이 <strong>함께</strong> 이동합니다.
                </p>
                <b-badge v-for="p in preview.affected_project_codes" :key="p"
                         variant="primary" class="mr-1"
                >
                    {{ p || '(프로젝트 없음)' }}
                </b-badge>
                <b-table v-if="preview.nsr_rows && preview.nsr_rows.length"
                         :items="preview.nsr_rows" :fields="nsrFields" small striped
                         responsive class="mt-2 mb-0"
                />
            </b-card>

            <div class="text-right mb-4">
                <b-button variant="link" :disabled="isBusy" @click="resetAll">초기화</b-button>
                <b-button variant="danger" :disabled="isBusy" @click="doExecute">
                    <b-spinner v-if="executing" small class="mr-1"/>
                    재배정 실행
                </b-button>
            </div>
        </template>

        <!-- ── 실행 결과 ── -->
        <b-card v-if="result" border-variant="success" class="mb-3">
            <h5 class="text-success mb-3">
                <i class="bi bi-check-circle-fill mr-1"></i>재배정 완료
                <small class="text-muted ml-2">이력 #{{ result.log_id }}</small>
            </h5>
            <b-row class="mb-3">
                <b-col md="4">유지된 답변: <strong>{{ result.keep_count }}</strong></b-col>
                <b-col md="4">삭제된 답변: <strong>{{ result.delete_count }}</strong></b-col>
                <b-col md="4">삭제된 평가: <strong>{{ result.evaluation_delete_count }}</strong></b-col>
            </b-row>
            <b-button size="sm" variant="outline-secondary" @click="$emit('go-history', result.log_id)">
                실행 이력에서 백업 확인
            </b-button>
        </b-card>
    </div>
</template>

<script>
/**
 * 답변 보존 캠페인 재배정 — 실행 탭.
 *
 * ⛔ 이 화면의 규율: preview 응답의 execute_requires 를 **그대로** execute 로 넘긴다.
 *    삭제 건수·영향 프로젝트를 화면에서 다시 계산하지 않는다. 서버가 자기 계산과
 *    대조해 불일치 시 거절하는데, 화면이 임의로 만든 값을 보내면 "확인했다"는 신호가
 *    거짓이 되기 때문이다(그 확인이 유일한 오조작 방지선이다).
 *
 * URL: ?period=(최신 기간이면 생략)&project_code=&campaign_id=&company_id= (현재 캠페인 + 업체).
 *    변경 후 캠페인·미리보기·실행 결과는 URL 에 싣지 않는다 — 되돌릴 수 없는 작업의 대상은 매번 직접 고른다.
 *    이력 탭(?tab=history)일 때는 이 탭이 URL 을 읽지도 쓰지도 않는다 (이력 탭의 company_id 는 필터라 뜻이 다르다).
 */
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import {
    executeRemap,
    previewRemap,
    remapErrorMessage,
} from '@shared_utils/api/campaignRemap';

const OWN_KEYS = ['period', 'project_code', 'campaign_id', 'company_id'];
// vue-router 는 같은 키가 여러 번이면 배열을 준다 — 빈 값이 아닌 문자열 하나일 때만 값으로 본다
const queryStr = v => (typeof v === 'string' && v !== '' ? v : null);
const queryId = v => {
    const n = Number(queryStr(v));
    return Number.isInteger(n) && n > 0 ? n : null;
};
const sameQuery = (a, b) => {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every(k => String(a[k]) === String(b[k]));
};

export default {
    name: 'RemapExecuteTab',
    components: {AutoCompleteSelect},
    data() {
        return {
            periods: [],
            selectedPeriod: null,
            loadingPeriods: false,
            projects: [],
            selectedProjectCode: null,
            companies: [],
            selectedCompanyKey: null,
            campaigns: [],
            toCampaignId: null,
            preview: null,
            result: null,
            showDeleteList: false,
            loadingProjects: false,
            loadingCompanies: false,
            loadingCampaigns: false,
            previewLoading: false,
            executing: false,
            // 늦게 온 응답·지난 복원을 버리기 위한 요청 순번
            restoreSeq: 0,
            projectsSeq: 0,
            campaignsSeq: 0,
            companiesSeq: 0,
            previewSeq: 0,
            // 지금 기간의 목록 / 지금 프로젝트의 업체 목록 조회 (성공하면 true)
            periodListsPromise: null,
            companiesPromise: null,
            nsrFields: [
                {key: 'campaign_id', label: '캠페인 ID'},
                {key: 'project_code', label: '프로젝트'},
                {key: 'status_code', label: '상태'},
            ],
        };
    },
    computed: {
        isBusy() {
            return this.previewLoading || this.executing;
        },
        /** auto-complete-select 규약: {id, text}. 검색 필터는 text 만 본다. */
        projectOptions() {
            const seen = new Set();
            const out = [];
            for (const group of this.projects || []) {
                for (const p of (group.projects || [group])) {
                    const code = p.project_code;
                    if (!code || seen.has(code)) continue;
                    seen.add(code);
                    const name = p.project_name || p.name || '';
                    out.push({
                        id: code,
                        // 코드로도 이름으로도 검색되게 둘 다 담는다.
                        text: name && name !== code ? `${name} (${code})` : code,
                        // 정렬 전용 — 옵션 규약({id,text}) 밖 필드라 표시에는 안 쓰인다.
                        sortKey: Number(p.id) || 0,
                    });
                }
            }
            // 최신(=id 큰 것)이 위로. common-data.getProjectCodeOptions 와 같은 규칙.
            return out.sort((a, b) => b.sortKey - a.sortKey);
        },
        companyOptions() {
            return (this.companies || [])
                .filter(c => c.campaign_id && c.company_id)
                .map(c => ({
                    // 같은 업체가 캠페인별로 여러 행일 수 있어 복합키를 쓴다.
                    id: `${c.campaign_id}-${c.company_id}`,
                    // ⛔ 업체명과 company_id 를 함께 담아야 둘 다로 검색된다(필터가
                    //    text 만 보기 때문). 업체가 수백 개인 프로젝트에서 이게 핵심.
                    text: `${c.company_name || ''} (${c.company_id})`
                        + (c.campaign_name ? ` — ${c.campaign_name}` : ''),
                    campaign_id: c.campaign_id,
                    company_id: c.company_id,
                    campaign_name: c.campaign_name || '',
                    company_name: c.company_name || '',
                }))
                .sort((a, b) => a.text.localeCompare(b.text));
        },
        selectedProject() {
            return (this.projectOptions || []).find(p => p.id === this.selectedProjectCode) || null;
        },
        selectedCompany() {
            return (this.companyOptions || []).find(c => c.id === this.selectedCompanyKey) || null;
        },
        toCampaignOptions() {
            // 현재 캠페인은 대상에서 제외 (from == to 는 서버가 422 로 거절한다)
            const fromId = this.selectedCompany && this.selectedCompany.campaign_id;
            // 최신(=id 큰 것)이 위로. filter 가 새 배열을 주므로 sort 가 원본을 안 건드린다.
            return (this.campaigns || [])
                .filter(c => c.id && c.id !== fromId)
                .sort((a, b) => b.id - a.id);
        },
        fromCampaignText() {
            if (!this.selectedCompany) return '';
            const {campaign_name: name, campaign_id: id} = this.selectedCompany;
            return name ? `${name} (${id})` : `${id}`;
        },
        projectPlaceholder() {
            if (this.loadingProjects) return '불러오는 중...';
            return this.projectOptions.length ? '프로젝트를 선택하세요' : '프로젝트가 없습니다';
        },
        companyPlaceholder() {
            if (this.loadingCompanies) return '불러오는 중...';
            if (!this.selectedProjectCode) return '프로젝트를 먼저 선택하세요';
            return this.companyOptions.length ? '업체를 선택하세요' : '업체가 없습니다';
        },
        toCampaignPlaceholder() {
            if (this.loadingCampaigns) return '불러오는 중...';
            if (!this.selectedCompany) return '업체를 먼저 선택하세요';
            return this.toCampaignOptions.length ? '변경할 캠페인을 선택하세요' : '캠페인이 없습니다';
        },
        canPreview() {
            return !!(this.selectedCompany && this.toCampaignId);
        },
        /** 최신 기간. 응답 순서에 기대지 않고 값('2026_1H' 꼴)으로 고른다. */
        defaultPeriod() {
            return [...this.periods].sort().pop() || null;
        },
        stateQuery() {
            const q = {};
            if (this.selectedPeriod && this.selectedPeriod !== this.defaultPeriod) q.period = this.selectedPeriod;
            if (this.selectedProjectCode) q.project_code = this.selectedProjectCode;
            if (this.selectedCompany) {
                q.campaign_id = String(this.selectedCompany.campaign_id);
                q.company_id = String(this.selectedCompany.company_id);
            }
            return q;
        },
    },
    watch: {
        // 같은 화면 안의 query 변화(뒤로가기·링크). 이력 탭으로 가는 중이면 진행 중인 복원도 버린다
        '$route.query'() {
            if (!this.isActive()) {
                this.restoreSeq++;
                return;
            }
            this.applyQuery();
        },
    },
    async mounted() {
        await this.fetchPeriods();
        this.applyQuery();
    },
    beforeDestroy() {
        // 다른 화면으로 떠난 뒤 늦게 끝난 복원이 그 화면의 URL 을 고치지 않게 한다
        this.left = true;
        this.restoreSeq++;
    },
    methods: {
        isActive() {
            return !this.left && this.$route.query.tab !== 'history';
        },
        async fetchPeriods() {
            this.loadingPeriods = true;
            try {
                const {data} = await this.$axios.get('/assessments/periods');
                this.periods = (Array.isArray(data) ? data : []).filter(p => typeof p === 'string' && p);
            } catch (e) {
                if (e?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', '조회 오류', '기간 목록을 불러오지 못했습니다.');
            } finally {
                this.loadingPeriods = false;
            }
        },
        /**
         * URL → 화면. 기간 → 프로젝트 → 업체 순으로 목록에 있는지 확인하며 고른다.
         * 값이 실제로 바뀐 단계만 하위 선택을 비운다 — 이력 탭에 다녀와 같은 query 로 돌아오면
         * 변경 후 캠페인·미리보기가 그대로 남는다. 목록에 없는 키(와 그 아래 키)는 replace 로 지우되,
         * 목록 조회가 실패했으면 URL 을 고치지 않는다.
         */
        async applyQuery() {
            if (!this.periods.length || !this.isActive()) return;
            const seq = ++this.restoreSeq;
            const q = this.$route.query;

            const period = this.periods.includes(queryStr(q.period)) ? queryStr(q.period) : this.defaultPeriod;
            if (period !== this.selectedPeriod) {
                this.selectedPeriod = period;
                this.clearProject();
                this.loadPeriodLists();
            }
            const listsOk = await this.periodListsPromise;
            if (seq !== this.restoreSeq || !listsOk) return;

            const code = queryStr(q.project_code);
            const project = code && this.projectOptions.some(p => p.id === code) ? code : null;
            if (project !== this.selectedProjectCode) {
                this.selectedProjectCode = project;
                this.clearCompany();
                this.loadCompanies();
            }
            const companiesOk = await this.companiesPromise;
            if (seq !== this.restoreSeq || !companiesOk) return;

            const campaignId = queryId(q.campaign_id);
            const companyId = queryId(q.company_id);
            const wanted = campaignId && companyId ? `${campaignId}-${companyId}` : null;
            const key = wanted && this.companyOptions.some(c => c.id === wanted) ? wanted : null;
            if (key !== this.selectedCompanyKey) {
                this.selectedCompanyKey = key;
                this.toCampaignId = null;
                this.resetPreview();
            }
            this.syncQuery('replace');
        },
        /** 화면 → URL. 사용자 선택은 push, 자동 보정은 replace. 이 탭의 키만 바꾼다. */
        syncQuery(mode) {
            if (!this.isActive()) return;
            const query = {...this.$route.query};
            OWN_KEYS.forEach(k => delete query[k]);
            delete query.tab; // 실행 탭은 기본 탭 — ?tab=execute 같은 값은 지운다
            Object.assign(query, this.stateQuery);
            if (sameQuery(query, this.$route.query)) return;
            this.$router[mode]({query}).catch(() => {});
        },
        // URL 복원에는 프로젝트 목록만 필요하다 (변경 후 캠페인은 URL 에 없음)
        loadPeriodLists() {
            this.periodListsPromise = Promise.all([this.fetchProjects(), this.fetchCampaigns()])
                .then(([projectsOk]) => projectsOk);
            return this.periodListsPromise;
        },
        loadCompanies() {
            this.companiesPromise = this.fetchCompanies();
            return this.companiesPromise;
        },
        clearProject() {
            this.selectedProjectCode = null;
            this.clearCompany();
            this.loadCompanies();
        },
        clearCompany() {
            this.companies = [];
            this.selectedCompanyKey = null;
            this.toCampaignId = null;
            this.resetPreview();
        },
        // 성공 true. 기간을 바꿔 다시 부르면 이전 수집은 중간에 멈추고 false (상태를 건드리지 않는다)
        async fetchProjects() {
            const seq = ++this.projectsSeq;
            const period = this.selectedPeriod;
            this.projects = [];
            this.loadingProjects = true;
            try {
                // /projects 는 per_page 상한(<=100)이 있어 페이지를 끝까지 수집한다.
                const PER_PAGE = 100;
                const items = [];
                let page = 1;
                let pages = 1;
                let total = Infinity;
                for (;;) {
                    const {data} = await this.$axios.get('/projects', {
                        params: {period, page, per_page: PER_PAGE},
                    });
                    if (seq !== this.projectsSeq) return false;
                    const pageItems = (data && data.items) || [];
                    items.push(...pageItems);
                    pages = (data && data.pages) || page;
                    if (data && typeof data.total === 'number') total = data.total;
                    if (pageItems.length === 0 || items.length >= total || page >= pages) break;
                    page += 1;
                }
                this.projects = items;
                return true;
            } catch (e) {
                if (seq !== this.projectsSeq || e?.response?.status === 401) return false;
                this.$EventBus.$emit('toast-failure', '조회 오류', '프로젝트 목록을 불러오지 못했습니다.');
                return false;
            } finally {
                if (seq === this.projectsSeq) this.loadingProjects = false;
            }
        },
        async fetchCampaigns() {
            const seq = ++this.campaignsSeq;
            this.campaigns = [];
            this.loadingCampaigns = true;
            try {
                const {data} = await this.$axios.get('/campaigns', {
                    params: {period: this.selectedPeriod},
                });
                if (seq !== this.campaignsSeq) return false;
                this.campaigns = (data && data.campaigns) || [];
                return true;
            } catch (e) {
                if (seq !== this.campaignsSeq || e?.response?.status === 401) return false;
                this.$EventBus.$emit('toast-failure', '조회 오류', '캠페인 목록을 불러오지 못했습니다.');
                return false;
            } finally {
                if (seq === this.campaignsSeq) this.loadingCampaigns = false;
            }
        },
        // 프로젝트가 없으면 바로 true
        async fetchCompanies() {
            const seq = ++this.companiesSeq;
            const projectCode = this.selectedProjectCode;
            this.companies = [];
            this.loadingCompanies = !!projectCode;
            if (!projectCode) return true;
            try {
                const {data} = await this.$axios.get('/assessments/results', {
                    params: {project_code: projectCode},
                });
                if (seq !== this.companiesSeq) return false;
                this.companies = Array.isArray(data) ? data : [];
                return true;
            } catch (e) {
                if (seq !== this.companiesSeq || e?.response?.status === 401) return false;
                this.$EventBus.$emit('toast-failure', '조회 오류', '업체 목록을 불러오지 못했습니다.');
                return false;
            } finally {
                if (seq === this.companiesSeq) this.loadingCompanies = false;
            }
        },
        // ── 사용자 선택 (push). 진행 중인 URL 복원은 버린다 ──
        onPeriodChange() {
            this.restoreSeq++;
            this.clearProject();
            this.loadPeriodLists();
            this.syncQuery('push');
        },
        onProjectChange() {
            this.restoreSeq++;
            this.clearCompany();
            this.loadCompanies();
            this.syncQuery('push');
        },
        onCompanyChange() {
            this.restoreSeq++;
            this.toCampaignId = null;
            this.resetPreview();
            this.syncQuery('push');
        },
        resetPreview() {
            // 도착 전인 미리보기 응답도 버린다 (응답이 오기 전에 대상이 바뀐 경우)
            this.previewSeq++;
            this.preview = null;
            this.result = null;
            this.showDeleteList = false;
        },
        resetAll() {
            this.resetPreview();
            this.toCampaignId = null;
        },
        async doPreview() {
            if (!this.canPreview) return;
            const seq = ++this.previewSeq;
            this.previewLoading = true;
            this.result = null;
            try {
                const preview = await previewRemap({
                    companyId: this.selectedCompany.company_id,
                    fromCampaignId: this.selectedCompany.campaign_id,
                    toCampaignId: this.toCampaignId,
                });
                if (seq === this.previewSeq) this.preview = preview;
            } catch (e) {
                if (seq !== this.previewSeq || e?.response?.status === 401) return;
                this.preview = null;
                this.$EventBus.$emit(
                    'toast-failure', '미리보기 실패',
                    remapErrorMessage(e, '미리보기를 가져오지 못했습니다.'),
                );
            } finally {
                this.previewLoading = false;
            }
        },
        async doExecute() {
            if (!this.preview || !this.selectedCompany) return;
            // 확인창이 떠 있는 동안 선택이 바뀌어도(뒤로가기 등) 확인한 대상으로만 실행한다
            const p = this.preview;
            const target = this.selectedCompany;
            const toCampaignId = this.toCampaignId;
            // ⛔ 줄바꿈(\n)은 렌더되지 않는다(white-space 클래스가 프로젝트에 없음).
            //    기존 확인 모달 관례대로 한 문단으로 잇되, 삭제 건수는 반드시 포함한다.
            const message = `${target.text} 의 캠페인을 `
                + `${p.from_campaign_name} 에서 ${p.to_campaign_name} 으로 변경합니다. `
                + `답변 ${p.keep_count}건은 유지되고, `
                + `${p.delete_count}건은 영구 삭제됩니다`
                + (p.evaluation_delete_count
                    ? ` (감사자 평가 ${p.evaluation_delete_count}건도 함께 삭제되어 재평가가 필요합니다)`
                    : '')
                + '. 이 작업은 되돌릴 수 없습니다. 실행하시겠습니까?';
            const ok = await this.$bvModal.msgBoxConfirm(message, {
                title: '재배정 실행 확인',
                okVariant: 'danger',
                okTitle: '재배정 실행',
                cancelTitle: '취소',
                centered: true,
            });
            if (!ok) return;
            // 선택이 바뀌면 미리보기가 비워진다 — 확인한 미리보기가 아니면 실행하지 않는다
            if (this.preview !== p) {
                this.$EventBus.$emit('toast-warning', '실행 취소', '대상이 바뀌어 실행하지 않았습니다. 미리보기를 다시 실행하세요.');
                return;
            }

            this.executing = true;
            let result;
            try {
                // ⛔ execute_requires 를 그대로 전달 — 화면에서 재계산 금지.
                result = await executeRemap({
                    companyId: target.company_id,
                    fromCampaignId: target.campaign_id,
                    toCampaignId,
                    executeRequires: p.execute_requires,
                });
            } catch (e) {
                this.executing = false;
                if (e?.response?.status === 401) return;
                // 409/422 는 서버가 실행하지 않은 것 — 낡은 건수가 남지 않게 미리보기를 비운다.
                const stale = [409, 422].includes(e?.response?.status);
                if (stale) this.resetPreview();
                this.$EventBus.$emit(
                    'toast-failure', '재배정 실패',
                    remapErrorMessage(e, '재배정에 실패했습니다.')
                        + (stale ? ' 미리보기를 다시 실행하세요.' : ''),
                );
                return;
            }
            this.preview = null;
            this.$EventBus.$emit('toast-success', '재배정 완료', `${target.text} 의 캠페인을 변경했습니다.`);
            this.$emit('executed');
            // 이동 후에는 업체의 현재 캠페인이 바뀌었으므로 업체 목록을 다시 읽는다(업체 선택은 비워짐).
            // 다시 읽기가 결과 카드도 비우므로 결과는 그 뒤에 채운다. 다시 읽기가 실패해도 결과는 보인다.
            // 다시 읽는 동안에도 잠가 두고, 그새 선택이 바뀌었으면(뒤로가기 등) 다른 대상 화면에 결과를 띄우지 않는다.
            const seq = ++this.restoreSeq;
            this.clearCompany();
            try {
                await this.loadCompanies();
            } finally {
                this.executing = false;
            }
            if (seq !== this.restoreSeq) return;
            this.syncQuery('replace');
            this.result = result;
        },
    },
};
</script>

<style scoped>
.summary-box {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 12px 8px;
}

.summary-label {
    font-size: 0.8rem;
    color: #6c757d;
}

.summary-value {
    font-size: 1.5rem;
    font-weight: 600;
}

.delete-list {
    max-height: 220px;
    overflow-y: auto;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}
</style>
